import React, { Component } from 'react';
import { NavLink, useParams, useLocation } from 'react-router-dom';
import { Button, Card, CardContent, Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, Field, FormSpy } from 'react-final-form';
import dayjs from 'dayjs-ext';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { find, get } from 'lodash';
import { getUser } from 'src/user';
import {
  setBreadcrumbs,
  UiSelectAdapter,
  UiInputAdapter,
  InnerLoader,
  UiDatePickerAdapter,
  SaveChangesToast,
  ConfirmChangesModal,
  reduceServerErrors,
  UiToastAdapter,
  UiFieldset,
  UiToggleAdapter,
  UiTooltip,
  UiQuillAdapter,
} from 'src/ui';

import {
  getAccount,
  fetchAccount,
  fetchFormData,
  getFormData,
  fetchAccounts,
  getAccounts,
} from 'src/account';
import { getAudits } from '../redux';
import { fetchAudits, createAudit, updateAudit } from '../redux/actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { composeValidators, required } from 'src/ui/validations';

const AUDIT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
];

// HOC to inject params and location for React Router v6 compatibility
function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const params = useParams();
    const location = useLocation();
    const match = React.useMemo(() => ({
      params,
      url: location.pathname,
    }), [params, location.pathname]);
    return <Component {...props} match={match} />;
  }
  return ComponentWithRouterProp;
}

class AuditEditPage extends Component {
  constructor(props) {
    super(props);
    const { user } = props;
    
    // Use user.accountId if available (account-specific context)
    const accountId = user?.accountId || null;
    
    // Track which account we've already fetched to prevent re-fetching
    this._lastFetchedAccount = accountId;
    
    this.state = {
      loading: true,
      loadingAccount: false,
      title: '',
      initialValues: {
        status: 'pending',
        next_audit_date: dayjs().add(1, 'year').startOf('day').toDate(),
        ...(accountId ? { account: accountId } : {}),
      },
      confirmChangesModal: false,
    };
  }

  async componentDidMount() {
    const { match, setBreadcrumbs, fetchAccount, fetchAccounts, fetchAudits, user, account } = this.props;
    const { id } = match.params;
    const title = id ? 'Edit' : 'Add';

    this.setState({ title });

    setBreadcrumbs([
      { label: 'Audits', href: '/audits' },
      { label: title, href: match.url },
    ]);

    const fiId = user.financialInstitutionId || user.fiSelectedAccountId;

    if (fiId) {
      // Fetch existing audits first
      await fetchAudits();

      // If editing an existing audit, fetch that audit's account to get locations
      if (id) {
        const auditId = +id;
        const { audits } = this.props;
        const audit = audits.audits?.find(a => a.id === auditId);
        
        if (audit?.account) {
          await fetchAccount(audit.account);
        }
        
        this.buildData(auditId);
      } else {
        // If adding a new audit
        if (user.accountId) {
          // Fetch account data to get locations (account-specific context)
          await fetchAccount(user.accountId);
        } else {
          // If no specific account, fetch all accounts for selection (bank admin/super admin)
          await fetchAccounts({ financialInstitutionId: fiId, userId: user.id });
        }
      }
    }

    this.setState({ loading: false });
  }

  buildData = auditId => {
    const { audits } = this.props;
    const audit = audits.audits?.find(a => a.id === auditId);

    if (audit) {
      this.setState({
        initialValues: {
          id: audit.id,
          account: audit.account,
          location: audit.location,
          next_audit_date: audit.next_audit_date
            ? dayjs(audit.next_audit_date).utc().toDate()
            : null,
          last_audit_date: audit.last_audit_date
            ? dayjs(audit.last_audit_date).utc().toDate()
            : null,
          status: audit.status,
          notes: audit.notes,
        },
      });
    }
  };

  handleSubmit = async values => {
    const { match, account, user } = this.props;
    const { id } = match.params;
    
    // Transform form values to API format
    const apiValues = {
      // For account, we need to send the FinancialInstitutionAccount ID, not Account ID
      // The account value from the form is the FinancialInstitutionAccount ID
      account: values.account?.value || values.account,
      location: values.location?.value || values.location,
      status: values.status?.value || values.status,
      next_audit_date: values.next_audit_date 
        ? dayjs(values.next_audit_date).format('YYYY-MM-DD')
        : null,
      last_audit_date: values.last_audit_date 
        ? dayjs(values.last_audit_date).format('YYYY-MM-DD')
        : null,
      notes: values.notes || '',
    };
    
    if (id) {
      await this.updateAudit(id, apiValues);
    } else {
      await this.addAudit(apiValues);
    }
    this.setState({
      initialValues: values,
    });
  };

  showModal = () => {
    this.toggleModal(true);
  };

  hideModal = () => {
    this.toggleModal(false);
  };

  toggleModal = value => {
    this.setState({ confirmChangesModal: value });
  };

  async addAudit(formValues) {
    const { createAudit } = this.props;

    const result = await createAudit(formValues);

    const { audits } = this.props;

    if (audits.isError()) {
      return reduceServerErrors(audits);
    }

    toast(UiToastAdapter('Audit added successfully!', 'success'));
    return result;
  }

  async updateAudit(id, formValues) {
    const { updateAudit } = this.props;

    await updateAudit(id, formValues);

    const { audits } = this.props;

    if (audits.isError()) {
      return reduceServerErrors(audits);
    }

    toast(UiToastAdapter('Audit updated successfully!', 'success'));
  }

  getLocationsForAccount = (accountValue) => {
    const { accounts, account } = this.props;
    
    // Extract the account ID (could be an object with 'value' property or just the ID)
    const accountId = accountValue?.value || accountValue;
    
    // If we have the full account data (from fetchAccount)
    if (account?.id === accountId && account?.locations) {
      return account.locations.map(location => ({
        value: location.id,
        label: `${location.address}, ${location.city}, ${location.state}`,
      }));
    }
    
    // Note: The accounts from fetchAccounts don't include locations
    // We need to fetch the full account details when an account is selected
    return [];
  };

  handleAccountChange = async (accountValue) => {
    const { fetchAccount } = this.props;
    
    // Extract the account ID (could be an object with 'value' property or just the ID)
    const accountId = accountValue?.value || accountValue;
    
    // Fetch full account details to get locations
    if (accountId && typeof accountId === 'number') {
      this.setState({ loadingAccount: true });
      await fetchAccount(accountId);
      this.setState({ loadingAccount: false });
    }
  };

  render() {
    const { loading, loadingAccount, title, initialValues, confirmChangesModal } = this.state;
    const { accounts, account, user } = this.props;

    // Determine if we're in account-specific context or global context
    const isAccountSpecific = !!user.accountId;
    
    // Build account options from all accounts
    let accountOptions = [];
    
    if (isAccountSpecific && account?.id) {
      // Account-specific context: only show the user's account
      const accountLabel = account.businessName || account.name || `Account #${account.id}`;
      accountOptions = [{
        value: account.id,
        label: accountLabel,
      }];
    } else if (!isAccountSpecific && accounts.items && accounts.items.length > 0) {
      // Global context (bank admin/super admin): show all accounts
      accountOptions = accounts.items.map(acc => ({
        value: acc.id,
        label: acc.businessName || acc.name || `Account #${acc.id}`,
      }));
    }

    // Helper function to get locations for a selected account ID
    const getLocationOptionsForAccount = (accountValue) => {
      // Extract the account ID (could be an object with 'value' property or just the ID)
      const accountId = accountValue?.value || accountValue;
      
      // If we have the full account data (from fetchAccount)
      if (account?.id === accountId && account?.locations) {
        return account.locations.map(location => ({
          value: location.id,
          label: `${location.address}, ${location.city}, ${location.state}`,
        }));
      }
      
      return [];
    };

    const toastContainerClassName = classNames('toast-container', {
      'toast-container--visible': true,
    });

    return (
      <>
        <MuiTitle isSize="4" hasTextColor="info" tag="h2">
          {`${title} Audit`}
          <NavLink
            className="close-page button is-small is-text is-pulled-right"
            to="/audits"
          >
            <span className="icon has-text-danger">
              <FontAwesomeIcon icon={['fal', 'times-circle']} size="2x" />
            </span>
          </NavLink>
        </MuiTitle>

        <Form
          key={initialValues.account || 'no-account'} // Force remount when account changes
          onSubmit={this.handleSubmit}
          decorators={[]}
          subscription={{
            submitting: true,
            hasValidationErrors: true,
            dirty: true,
            dirtySinceLastSubmit: true,
          }}
          initialValues={initialValues}
          render={({
            handleSubmit,
            submitting,
            hasValidationErrors,
            dirty,
            dirtySinceLastSubmit,
          }) => (
            <Card tag="form" noValidate onSubmit={handleSubmit}>
            <CardContent>
              <div className={toastContainerClassName}>
                <SaveChangesToast
                  message="Click the button to the right to save your changes"
                  isLoading={submitting}
                  isDisabled={submitting || hasValidationErrors}
                  isVisible={dirty || dirtySinceLastSubmit}
                  onClickButton={this.showModal}
                />
              </div>
              <ConfirmChangesModal
                title="Audit"
                isActive={confirmChangesModal}
                onClickCancel={this.hideModal}
                onClickSubmit={this.hideModal}
              />
              <InnerLoader visible={submitting || loading || loadingAccount} />
              <UiFieldset title="Audit Details" isFirst>
                <Columns isMultiline>
                  <Column isSize="1/2">
                    <FormSpy subscription={{ values: true }}>
                      {({ values }) => {
                        // Track account changes and fetch account details
                        // Using a ref-like pattern to avoid re-fetching on every render
                        const accountValue = values.account?.value || values.account;
                        const initialAccountValue = initialValues.account?.value || initialValues.account;
                        
                        // Check if we need to fetch account details (only in global context when account changes)
                        if (!isAccountSpecific && accountValue && accountValue !== initialAccountValue && accountValue !== this._lastFetchedAccount) {
                          this._lastFetchedAccount = accountValue;
                          // Use setTimeout to avoid setState during render
                          setTimeout(() => this.handleAccountChange(values.account), 0);
                        }
                        
                        return (
                          <Field
                            component={UiSelectAdapter}
                            name="account"
                            id="account"
                            required
                            options={accountOptions}
                            label="Account"
                            validate={composeValidators('Account', required)}
                            isDisabled={isAccountSpecific || !!initialValues.id} // Disable if account-specific context or editing
                            getOptionValue={option => option.value}
                            getOptionLabel={option => option.label}
                            parse={value => value?.value || value} // Convert option object to ID for form state
                            format={value => {
                              // Convert ID to option object for react-select display
                              if (typeof value === 'number' || typeof value === 'string') {
                                return accountOptions.find(opt => opt.value === value) || null;
                              }
                              return value;
                            }}
                          />
                        );
                      }}
                    </FormSpy>
                  </Column>
                  <Column isSize="1/2">
                    <FormSpy subscription={{ values: true }}>
                      {({ values }) => {
                        const locationOptions = values.account 
                          ? getLocationOptionsForAccount(values.account)
                          : [];
                        
                        return (
                          <Field
                            component={UiSelectAdapter}
                            name="location"
                            id="location"
                            required
                            options={locationOptions}
                            label="Location"
                            validate={composeValidators('Location', required)}
                            isDisabled={!values.account}
                            getOptionValue={option => option.value}
                            getOptionLabel={option => option.label}
                            parse={value => value?.value || value} // Convert option object to ID for form state
                            format={value => {
                              // Convert ID to option object for react-select display
                              if (typeof value === 'number' || typeof value === 'string') {
                                return locationOptions.find(opt => opt.value === value) || null;
                              }
                              return value;
                            }}
                          />
                        );
                      }}
                    </FormSpy>
                  </Column>
                  <Column isSize="1/2">
                    <Field
                      component={UiDatePickerAdapter}
                      name="next_audit_date"
                      id="next_audit_date"
                      required
                      label="Next Audit Date"
                      validate={composeValidators('Next Audit Date', required)}
                    />
                  </Column>
                  <Column isSize="1/2">
                    <Field
                      component={UiDatePickerAdapter}
                      name="last_audit_date"
                      id="last_audit_date"
                      label="Last Audit Date"
                    />
                  </Column>
                  <Column isSize="1/2">
                    <Field
                      component={UiSelectAdapter}
                      name="status"
                      id="status"
                      required
                      options={AUDIT_STATUS_OPTIONS}
                      label="Status"
                      validate={composeValidators('Status', required)}
                      getOptionValue={option => option.value}
                      getOptionLabel={option => option.label}
                      parse={value => value?.value || value} // Convert option object to value for form state
                      format={value => {
                        // Convert value to option object for react-select display
                        if (typeof value === 'string') {
                          return AUDIT_STATUS_OPTIONS.find(opt => opt.value === value) || null;
                        }
                        return value;
                      }}
                    />
                  </Column>
                </Columns>
              </UiFieldset>
              <UiFieldset title="Notes">
                <Field
                  component={UiQuillAdapter}
                  name="notes"
                  id="notes"
                  label="Audit Notes"
                  placeholder="Enter audit notes, findings, or follow-up actions..."
                />
              </UiFieldset>
            </CardContent>
          </Card>
        )}
      />
      </>
    );
  }
}

AuditEditPage.propTypes = {
  match: propTypes.object.isRequired,
  fetchAccount: propTypes.func.isRequired,
  fetchAccounts: propTypes.func.isRequired,
  fetchAudits: propTypes.func.isRequired,
  createAudit: propTypes.func.isRequired,
  updateAudit: propTypes.func.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  user: propTypes.object.isRequired,
  account: propTypes.object.isRequired,
  accounts: propTypes.object.isRequired,
  audits: propTypes.object.isRequired,
};

function mapStateToProps(state) {
  return {
    user: getUser(state),
    account: getAccount(state),
    accounts: getAccounts(state),
    audits: getAudits(state),
  };
}

const connectedAuditEditPage = connect(mapStateToProps, {
  setBreadcrumbs,
  fetchAccount,
  fetchAccounts,
  fetchAudits,
  createAudit,
  updateAudit,
})(withRouter(AuditEditPage));

export { connectedAuditEditPage as AuditEditPage };